import React from 'react';
import PropTypes from 'prop-types';
import {Image} from '@learningblocks/shared-components';
import {CardModalWindow} from '../SharedComponents/CardModalWindow';
import {CardModalContent} from '../SharedComponents/CardModalContent';
import {RenderHTML} from '@learningblocks/shared-components';
import LightBulb from '@common/images/lighbulb.svg';
import MoveCard from '@common/images/move.svg';
import TrashCan from '@common/images/mini-trash-icon.svg';
import './style.scss';

const ENTER_KEY = 13;

/**
 * Experimental-Design FullWidthCard component:
 */
export function FullWidthCard(props) {
  const [isModalOpen, toggleModal] = React.useState(false);

  const onKeyDown = (cb) => (event) => event.keyCode === ENTER_KEY && cb(event);

  const setModalOpen = () => {
    toggleModal(true);
  };

  const setModalClose = () => {
    toggleModal(false);
  };

  const infoButtonClick = () => {
    props.openGlossary ? props.openGlossary(props.title) : setModalOpen();
  };

  const modalButton = () => (
    <div
      tabIndex={props.tabIndex}
      className='card-icons__modal'
      onClick={infoButtonClick}
      onKeyDown={onKeyDown(setModalOpen)}>
      <Image src={LightBulb} alt='open-modal-window' />
    </div>
  );

  const addButton = () =>
    props.addCard && (
      <div
        tabIndex={props.tabIndex}
        className='card-icons__add'
        onClick={props.addCard}
        onKeyDown={onKeyDown(props.addCard)}>
        <Image src={MoveCard} alt='add-card-option' />
      </div>
    );

  const removeButton = () =>
    props.removeCard && (
      <div
        tabIndex={props.tabIndex}
        className='card-icons__remove'
        onClick={props.removeCard}
        onKeyDown={onKeyDown(props.removeCard)}>
        <Image src={TrashCan} alt='trash-card-option' />
      </div>
    );

  return (
    <>
      <div className='card'>
        <div className='card-image'>
          <Image src={props.image} alt={props.title} />
        </div>
        <div className='card-content'>
          <RenderHTML content={props.title} />
          <RenderHTML content={props.description} />
        </div>
        <div className='card-icons'>
          {modalButton()}
          {addButton()}
          {removeButton()}
        </div>
      </div>
      <CardModalWindow
        isModalOpen={isModalOpen}
        modalTitle={props.modal.title}
        setModalClose={setModalClose}>
        <CardModalContent components={props.modal.components} />
      </CardModalWindow>
    </>
  );
}

FullWidthCard.propTypes = {
  /**
   * String representing type of component
   */
  type: PropTypes.string,
  /**
   * callback function to be executed when card is added as next step in DesignSection
   * Prop should be null if addCard icon not required
   */
  addCard: PropTypes.func,
  /**
   * callback function to be executed when card needs to be removed from design section tree
   * Prop should be null if removeCard icon not required
   */
  removeCard: PropTypes.func,
  /**
   * Url to the 1:1 Card image.
   */
  image: PropTypes.string.isRequired,
  /**
   * Title of the card
   */
  title: PropTypes.string.isRequired,
  /**
   * Description of the card
   */
  description: PropTypes.string.isRequired,
  /**
   * Data to be displayed in the modal
   */
  modal: PropTypes.object.isRequired,
  /**
   * TabIndex to set on Component
   */
  tabIndex: PropTypes.string,
  /**
   * Open Glossary on pressing the hint button. If not present, will open modal window
   */
  openGlossary: PropTypes.func,
};

FullWidthCard.defaultProps = {
  removeCard: null,
  addCard: null,
  type: 'FULL_WIDTH_CARD',
  image: '',
  title: '',
  description: '',
  modal: {components: [], title: ''},
  tabIndex: '0',
  openGlossary: undefined,
};
